import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, CheckCircle, PlusCircle, Calendar, Smartphone, Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { listMedications } from '@/lib/medications';
import { getToday, markDose, formatLocalTime, resolveDeviceTimezone } from '@/lib/doses';

function StatusBadge({ status }) {
  const map = {
    taken: { bg: '#E4F4EB', text: '#1F7A4A', border: '#1F7A4A', label: 'Taken' },
    skipped: { bg: '#E8ECEE', text: '#5C6B72', border: '#5C6B72', label: 'Skipped' },
    scheduled: { bg: '#EEF4F7', text: '#5B2D1F', border: '#D7E2E8', label: 'Scheduled' },
  };
  const c = map[status] || map.scheduled;
  return (
    <span className="inline-flex px-2.5 py-0.5 rounded-full border text-[11px] font-extrabold uppercase tracking-wide" style={{ backgroundColor: c.bg, color: c.text, borderColor: c.border }}>
      {c.label}
    </span>
  );
}

export default function TodayPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const tz = user?.timezone || resolveDeviceTimezone();

  const [entries, setEntries] = useState([]);
  const [meds, setMeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [doses, medList] = await Promise.all([getToday(tz), listMedications()]);
      setEntries(doses);
      setMeds(medList);
    } catch (e) {
      setError(e.message || 'Could not load today.');
    } finally {
      setLoading(false);
    }
  }, [tz]);

  useEffect(() => { load(); }, [load]);

  const onMark = async (entry, status) => {
    const key = `${entry.medication_id}-${entry.scheduled_for}`;
    setBusyKey(key);
    const prev = entries;
    setEntries((curr) => curr.map((e) => e.medication_id === entry.medication_id && e.scheduled_for === entry.scheduled_for ? { ...e, status } : e));
    try {
      await markDose(entry.medication_id, entry.scheduled_for, status);
    } catch (e) {
      setEntries(prev);
      setError(e.message || 'Could not save dose status.');
    } finally {
      setBusyKey(null);
    }
  };

  const onLogPRN = async (med) => {
    const nowIso = new Date().toISOString();
    setBusyKey(`prn-${med.id}-${nowIso}`);
    try {
      await markDose(med.id, nowIso, 'taken');
      await load();
    } catch (e) {
      setError(e.message || 'Could not log dose.');
    } finally {
      setBusyKey(null);
    }
  };

  const prnMeds = useMemo(() => meds.filter((m) => (m.frequency || '').toLowerCase() === 'as needed'), [meds]);
  const scheduledCount = entries.filter((e) => e.status === 'scheduled').length;
  const takenCount = entries.filter((e) => e.status === 'taken').length;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 pb-12" data-testid="today-screen">
      <div className="flex items-center gap-2 mb-1">
        <button onClick={() => navigate(-1)} data-testid="today-back-btn" className="p-1 rounded-lg hover:bg-black/5">
          <ChevronLeft className="h-6 w-6" style={{ color: '#5B2D1F' }} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-extrabold" style={{ color: '#5B2D1F' }} data-testid="today-screen-title">Today</h1>
          {!loading && <p className="text-xs mt-0.5" style={{ color: '#4B6B79' }}>{takenCount} taken · {scheduledCount} remaining · timezone {tz}</p>}
        </div>
      </div>

      {/* Web notification note */}
      <div data-testid="today-notifications-web-note" className="flex items-center gap-2 p-3 rounded-xl mb-4 mt-4" style={{ backgroundColor: '#EEF4F7' }}>
        <Smartphone className="h-4 w-4 shrink-0" style={{ color: '#5B2D1F' }} />
        <p className="text-sm" style={{ color: '#5B2D1F' }}>Reminders are delivered on the mobile app. The web preview just shows your schedule.</p>
      </div>

      {error && (
        <div data-testid="today-error" className="p-4 rounded-xl border mb-4" style={{ backgroundColor: '#FCE7EA', borderColor: '#B0303A' }}>
          <p className="font-semibold" style={{ color: '#B0303A' }}>{error}</p>
        </div>
      )}

      {loading && (
        <div className="flex flex-col items-center py-16 gap-3">
          <img src="/brand/logo-round.png" alt="" className="w-14 h-14 rounded-full opacity-35" />
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#D77A52' }} />
          <p className="text-base" style={{ color: '#4B6B79' }}>Loading today&apos;s doses…</p>
        </div>
      )}

      {!loading && entries.length === 0 && (
        <div data-testid="today-empty" className="rounded-2xl border p-6" style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}>
          <img src="/brand/logo-round.png" alt="" className="w-12 h-12 rounded-full opacity-35 mb-4" />
          <h2 className="text-xl font-extrabold mb-1" style={{ color: '#5B2D1F' }}>No scheduled doses today</h2>
          <p className="text-base" style={{ color: '#4B6B79' }}>Add medications with a schedule to see them here.</p>
        </div>
      )}

      {!loading && entries.length > 0 && (
        <div className="space-y-3 mt-2">
          {entries.map((e, idx) => {
            const key = `${e.medication_id}-${e.scheduled_for}`;
            const busy = busyKey === key;
            const isFinal = e.status !== 'scheduled';
            return (
              <div key={`${key}-${idx}`} data-testid={`today-dose-${idx}`} className="rounded-2xl border p-4 space-y-2" style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}>
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-extrabold truncate" style={{ color: '#0F2A35' }}>{e.medication_name}</p>
                    <p className="text-sm mt-0.5" style={{ color: '#4B6B79' }}>{e.dosage} · {formatLocalTime(e.scheduled_for, tz)}</p>
                  </div>
                  <StatusBadge status={e.status} />
                </div>
                {!isFinal && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => onMark(e, 'taken')}
                      disabled={busy}
                      data-testid={`today-dose-${idx}-taken`}
                      className="flex-1 flex items-center justify-center gap-1.5 min-h-[44px] rounded-xl text-base font-extrabold text-white transition-opacity hover:opacity-90 disabled:opacity-70"
                      style={{ backgroundColor: '#D77A52' }}
                    >
                      <CheckCircle className="h-4 w-4" /> Mark taken
                    </button>
                    <button
                      onClick={() => onMark(e, 'skipped')}
                      disabled={busy}
                      data-testid={`today-dose-${idx}-skip`}
                      className="flex-1 flex items-center justify-center gap-1.5 min-h-[44px] rounded-xl text-base font-extrabold border transition-opacity hover:opacity-90 disabled:opacity-70"
                      style={{ backgroundColor: '#EEF4F7', borderColor: '#D7E2E8', color: '#5B2D1F' }}
                    >
                      Skip
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {prnMeds.length > 0 && (
        <div data-testid="today-prn-section" className="mt-8 pt-6 border-t" style={{ borderColor: '#D7E2E8' }}>
          <h2 className="text-lg font-extrabold mb-1" style={{ color: '#5B2D1F' }}>As-needed (PRN)</h2>
          <p className="text-sm mb-3" style={{ color: '#4B6B79' }}>These aren&apos;t on a schedule — log a dose whenever you take one.</p>
          <div className="space-y-2">
            {prnMeds.map((m, idx) => {
              const busy = busyKey?.startsWith(`prn-${m.id}`);
              return (
                <div key={m.id} data-testid={`today-prn-${idx}`} className="flex items-center gap-4 rounded-2xl border p-4" style={{ backgroundColor: '#FFFFFF', borderColor: '#D7E2E8' }}>
                  <div className="flex-1 min-w-0">
                    <p className="text-base font-extrabold" style={{ color: '#0F2A35' }}>{m.name}</p>
                    <p className="text-sm" style={{ color: '#4B6B79' }}>{m.dosage}</p>
                  </div>
                  <button
                    onClick={() => onLogPRN(m)}
                    disabled={busy}
                    data-testid={`today-prn-${idx}-log`}
                    className="flex items-center gap-1.5 px-4 min-h-[44px] rounded-xl text-base font-extrabold text-white transition-opacity hover:opacity-90 disabled:opacity-70"
                    style={{ backgroundColor: '#D77A52' }}
                  >
                    <PlusCircle className="h-4 w-4" /> Log dose
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
